import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { ShoppingCart, Pill, Clock, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const DEFAULT_PRICE_DATA = {
  currentPrice: 0,
  officialPrice: 27.45,
  perCapsulePrice: 0.46,
  lastPriceScrape: null,
};

const AMAZON_PRODUCT_URL = "https://www.amazon.com/dp/B0DSV71JJ3";
const CORS_PROXIES = ["https://api.allorigins.win/raw?url="];
const API_URL = "https://api.zivbiolabs.com/api/prices";
const SCRAPE_INTERVAL = 30 * 60 * 1000; // 30 minutes in milliseconds

export const ProductDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [priceData, setPriceData] = useState(DEFAULT_PRICE_DATA);
  const [isScraping, setIsScraping] = useState(false);
  const [iconSize, setIconSize] = useState(30);

  const extractPriceFromDocument = useCallback((doc) => {
    try {
      const priceSelectors = [
        ".a-price .a-offscreen",
        "#priceblock_ourprice",
        ".a-price-whole",
        ".a-color-price",
        '[data-a-color="price"] .a-offscreen',
        ".priceToPay .a-offscreen",
      ];

      for (const selector of priceSelectors) {
        const priceElements = doc.querySelectorAll(selector);

        if (priceElements && priceElements.length > 0) {
          for (const element of priceElements) {
            const priceText = element.textContent.trim();
            const priceMatch = priceText.match(/[\d,.]+/);

            if (priceMatch) {
              const price = parseFloat(priceMatch[0].replace(/,/g, ""));
              if (!isNaN(price) && price > 0) {
                return {
                  success: true,
                  currentPrice: price,
                };
              }
            }
          }
        }
      }

      return {
        success: false,
        error: "Could not find price elements",
      };
    } catch (error) {
      console.error("Error extracting price:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }, []);

  const scrapePrice = useCallback(async () => {
    try {
      setIsScraping(true);

      let proxyWorked = false;
      let mainPageHtml = null;

      for (let i = 0; i < CORS_PROXIES.length; i++) {
        const currentProxy = CORS_PROXIES[i];
        try {
          const mainResponse = await fetch(
            `${currentProxy}${encodeURIComponent(AMAZON_PRODUCT_URL)}`,
            {
              headers: {
                "User-Agent":
                  "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36",
                Accept:
                  "text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8",
                "Accept-Language": "en-US,en;q=0.5",
                Referer: "https://www.google.com/",
                DNT: "1",
              },
              timeout: 10000,
            }
          );

          if (mainResponse.ok) {
            mainPageHtml = await mainResponse.text();

            if (
              mainPageHtml.includes("we couldn't find that page") ||
              mainPageHtml.includes("You need to enable JavaScript") ||
              mainPageHtml.includes("captcha")
            ) {
              continue;
            }

            proxyWorked = true;
            break;
          }
        } catch (proxyError) {
          console.error(`Proxy ${currentProxy} failed for price scraping:`, proxyError);
        }
      }

      if (!proxyWorked) {
        return {
          success: false,
          error: "All proxies failed",
          usingDefaultData: true,
        };
      }

      const parser = new DOMParser();
      const mainDoc = parser.parseFromString(mainPageHtml, "text/html");

      const extractedPriceData = extractPriceFromDocument(mainDoc);

      if (extractedPriceData.success) {
        const currentPrice = extractedPriceData.currentPrice;
        const officialPrice = DEFAULT_PRICE_DATA.officialPrice;

        const perCapsulePrice = (
          (currentPrice !== 0 ? currentPrice : officialPrice) / 60
        ).toFixed(2);

        const newPriceData = {
          currentPrice,
          officialPrice,
          perCapsulePrice,
          lastPriceScrape: new Date().toISOString(),
        };

        setPriceData(newPriceData);
        
        // Save to localStorage
        localStorage.setItem("priceData", JSON.stringify(newPriceData));
        
        // Update the API with the new price
        try {
          await fetch(API_URL, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ price: currentPrice }),
          });
        } catch (apiError) {
          console.error("Failed to update API with price:", apiError);
        }

        return {
          success: true,
          priceData: newPriceData,
        };
      } else {
        return {
          success: false,
          error: "Could not extract price data",
          usingDefaultData: true,
        };
      }
    } catch (error) {
      console.error("Error in scrapePrice:", error);
      return {
        success: false,
        error: error.message,
      };
    } finally {
      setIsScraping(false);
    }
  }, [extractPriceFromDocument]);

  const fetchApiPrice = useCallback(async () => {
    try {
      const response = await fetch(API_URL);
      if (response.ok) {
        const data = await response.json();
        if (data && data.price) {
          const currentPrice = data.price;
          const officialPrice = DEFAULT_PRICE_DATA.officialPrice;
          const perCapsulePrice = ((currentPrice !== 0 ? currentPrice : officialPrice) / 60).toFixed(2);
          
          const newPriceData = {
            currentPrice,
            officialPrice,
            perCapsulePrice,
            lastPriceScrape: new Date().toISOString(),
          };
          
          setPriceData(newPriceData);
          localStorage.setItem("priceData", JSON.stringify(newPriceData));
          return { success: true, priceData: newPriceData };
        }
      }
      throw new Error("Failed to fetch price from API");
    } catch (error) {
      console.error("Error fetching price from API:", error);
      return { success: false, error: error.message };
    }
  }, []);

  const shouldScrapePrice = useCallback(() => {
    const storedPriceData = localStorage.getItem("priceData");
    if (!storedPriceData) return true;
    
    try {
      const parsedData = JSON.parse(storedPriceData);
      if (!parsedData.lastPriceScrape) return true;
      
      const lastScrapeTime = new Date(parsedData.lastPriceScrape).getTime();
      const currentTime = new Date().getTime();
      
      return currentTime - lastScrapeTime > SCRAPE_INTERVAL;
    } catch (error) {
      console.error("Error parsing stored price data:", error);
      return true;
    }
  }, []);

  useEffect(() => {
    const initializePrice = async () => {
      // First try to load from localStorage
      const storedPriceData = localStorage.getItem("priceData");
      if (storedPriceData) {
        try {
          const parsedData = JSON.parse(storedPriceData);
          setPriceData(parsedData);
        } catch (error) {
          console.error("Error parsing stored price data:", error);
        }
      }
      
      // Then fetch from API (always do this to ensure we have the latest)
      await fetchApiPrice();
      
      // Check if we need to scrape for fresh data
      if (shouldScrapePrice()) {
        await scrapePrice();
      }
    };
    
    initializePrice();
  }, [fetchApiPrice, scrapePrice, shouldScrapePrice]);

  const formatPrice = (price) => {
    return price.toFixed(2);
  };

  const showPriceDifference =
    priceData.currentPrice > 0 &&
    Math.abs(priceData.currentPrice - priceData.officialPrice) > 0.01;

  const priceDifferencePercentage = Math.round(
    ((priceData.currentPrice - priceData.officialPrice) /
      priceData.officialPrice) *
      100
  );

  const isPriceIncrease = priceData.currentPrice > priceData.officialPrice;

  const updateIconSize = useCallback(() => {
    if (window.innerWidth < 768) {
      setIconSize(20);
    } else {
      setIconSize(30);
    }
  }, []);

  useEffect(() => {
    updateIconSize();
    window.addEventListener('resize', updateIconSize);
    
    return () => {
      window.removeEventListener('resize', updateIconSize);
    };
  }, [updateIconSize]);
  
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      <motion.div variants={itemVariants} className="space-y-1">
        <h1
          className="text-5xl lg:text-7xl text-#4d1312 [text-shadow:1px_0_0_white,2px_0_0_white,1px_1px_0_white,2px_2px_0_white]"
          style={{
            fontFamily: "Headlined, sans-serif",
          }}
        >
          TESTOSTERONE BOOSTER
        </h1>

        <motion.div
          variants={itemVariants}
          className="items-baseline space-x-4 block xl:hidden "
        >
          {showPriceDifference && (
            <span
              className={`px-3 py-1 ${
                isPriceIncrease ? "bg-blue-600" : "bg-red-600"
              } text-white rounded-full text-sm`}
            >
              {isPriceIncrease ? "+" : "-"}
              {Math.abs(priceDifferencePercentage)}%
            </span>
          )}

          {showPriceDifference ? (
            <span
              className={`text-2xl text-[#a41e24] ${
                isPriceIncrease ? "" : "font-bold"
              }`}
            >
              ${formatPrice(priceData.currentPrice)}
            </span>
          ) : (
            <span className="text-2xl text-[#a41e24] font-bold">
              ${formatPrice(priceData.officialPrice)}
            </span>
          )}

          {showPriceDifference && (
            <span
              className={`text-lg text-[#a41e24] ${
                isPriceIncrease ? "" : "line-through"
              }`}
            >
              ${formatPrice(priceData.officialPrice)}
            </span>
          )}

          <span className="text-sm text-[#a41e24]">
            (${priceData.perCapsulePrice} / Count)
          </span>
        </motion.div>

        <div className="flex margindiv flex-row items-center justify-between xl:mb-4 xl:justify-start xl:space-x-4">
          <div className="flex items-center gap-2">
            <Pill color="#a41e25" size={iconSize} className="md:w-6 md:h-6" />
            <span className="text-sm md:text-lg">60 Capsules</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar color="#a41e25" size={iconSize} className="md:w-6 md:h-6" />
            <span className="text-sm md:text-lg">30 serving</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock color="#a41e25" size={iconSize} className="md:w-6 md:h-6" />
            <span className="text-sm md:text-lg">2 capsules / Day</span>
          </div>
        </div>
      </motion.div>

      <motion.div
        variants={itemVariants}
        style={{marginTop:"1rem"}} 
        className="items-baseline space-x-4 hidden xl:flex"
      >
        {!isScraping && (
          <>
            {showPriceDifference && (
              <span
                className={`px-3 py-1 ${
                  isPriceIncrease ? "bg-blue-600" : "bg-red-600"
                } text-white rounded-full text-sm`}
              >
                {isPriceIncrease ? "+" : "-"}
                {Math.abs(priceDifferencePercentage)}%
              </span>
            )}

            {showPriceDifference ? (
              <span
                className={`text-2xl text-[#a41e24] ${
                  isPriceIncrease ? "" : "font-bold"
                }`}
              >
                ${formatPrice(priceData.currentPrice)}
              </span>
            ) : (
              <span className="text-2xl text-[#a41e24] font-bold">
                ${formatPrice(priceData.officialPrice)}
              </span>
            )}

            {showPriceDifference && (
              <span
                className={`text-lg text-[#a41e24] ${
                  isPriceIncrease ? "" : "line-through"
                }`}
              >
                ${formatPrice(priceData.officialPrice)}
              </span>
            )}

            <span className="text-[#a41e24] text-sm">
              (${priceData.perCapsulePrice} / Count)
            </span>
          </>
        )}
      </motion.div>

      <motion.div variants={itemVariants} style={{marginTop:"1rem", marginBottom: "3rem"}} className="flex items-center justify-between w-[250px]">
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-red-500 rounded-full" />
          <span>Veggie capsules</span>
        </div>
        <div className="flex mt-0 items-center space-x-2" style={{marginTop: "0rem"}}>
          <div className="w-2 h-2 bg-red-500 rounded-full" />
          <span>No filters</span>
        </div>
      </motion.div>

      <motion.p variants={itemVariants} className="text-gray-600">
        Experience the Difference: Unlock your peak potential with our premium
        testosterone booster, a unique blend of ancient herbs and modern
        science. We've harnessed the power of time-honored botanicals like
        Tribulus Terrestris, Ashwagandha, and Tongkat Ali, revered for their
        ability to support male vitality, and combined them with essential
        nutrients like Zinc and DIM. This meticulously crafted formula is
        designed to boost energy levels, enhance libido, and optimize physical
        performance, empowering you to conquer your day with confidence.
        Manufactured in a GMP-certified facility in the USA, our product
        undergoes rigorous third-party testing to ensure purity and potency. Get
        yours today!
      </motion.p>

      <motion.div variants={itemVariants} className="space-y-4">
        <a
          href="https://www.amazon.com/dp/B0DSV71JJ3"
          target="_blank"
          rel="noreferrer"
          className="w-full bg-gradient-primary text-white py-3 rounded-lg flex items-center justify-center space-x-2 hover:opacity-90 transition-opacity"
        >
          <ShoppingCart className="w-5 h-5" />
          <span>Shop on Amazon</span>
        </a>

        {location.pathname !== "/shop" && (
          <button
            type="button"
            className="w-full border border-gray-300 py-3 rounded-lg hover:bg-gray-50 transition-colors"
            onClick={() => navigate("/shop")}
          >
            Learn More
          </button>
        )}
      </motion.div>
    </motion.div>
  );
};